import React, { useEffect, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
import { useAdminDashboardService } from "../../services/adminDashboardService";
import { setCurrentPage, setDateRange } from "../../redux/adminDashboardSlice";
import { useDispatch, useSelector } from "react-redux";

const CustomDateRangePicker = ({
  isDatePickerOpen,
  setIsDatePickerOpen,
}) => {
  const dateRangeRef = useRef(null);
  const dispatch = useDispatch()
  const { fetchFilteredData, fetchOverAllRating,
    fetchOverAllRatingResult,
    fetchFeedbackComments,
    fetchFeedback1Rating,
    fetchFeedback2Rating,
    fetchFeedback3Rating } = useAdminDashboardService()

  const { selectedDateRange } = useSelector((state) => state.adminDashboard);

  const [selectionRange, setSelectionRange] = useState({
    startDate: null,
    endDate: null,
    key: "selection",
  });

  const [isDateRangeApplied, setIsDateRangeApplied] = useState(false);
  const value = selectedDateRange || { startDate: null, endDate: null, key: "selection" }

  // Adjusts date to local midnight (00:00 AM)
  const adjustDateToLocalMidnight = (date) => {
    const newDate = new Date(date);
    return newDate;
  };

  // Adjust end date to 12:00 PM
  const setEndDateToNoon = (date) => {
    const noonDate = new Date(date);
    noonDate.setHours(23, 59, 59, 999); // Set time to 12:00 PM
    return noonDate;
  };

  // Apply the date range with adjusted end date to 12 PM
  const applyDateRange = () => {
    const adjustedRange = {
      startDate: selectionRange.startDate
        ? adjustDateToLocalMidnight(selectionRange.startDate)
        : null,
      endDate: selectionRange.endDate
        ? setEndDateToNoon(selectionRange.endDate)
        : null,
      key: "selection",
    };

    setIsDateRangeApplied(false);
    dispatch(setDateRange(adjustedRange));
    dispatch(setCurrentPage(1));
    setIsDateRangeApplied(true);
    setIsDatePickerOpen(false);
  };

  const handleApplyApi = () => {
    fetchFilteredData()
    fetchOverAllRating()
    fetchOverAllRatingResult()
    fetchFeedbackComments()
    fetchFeedback1Rating()
    fetchFeedback2Rating()
    fetchFeedback3Rating()
  }

  const handleResetDate = () => {
    dispatch(setDateRange({ startDate: null, endDate: null, key: 'selection' }))
    setSelectionRange({ startDate: null, endDate: null, key: 'selection' })
  }

  useEffect(() => {
    if (isDateRangeApplied) {
      handleApplyApi();
      setIsDateRangeApplied(false);
    }
  }, [isDateRangeApplied, selectionRange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`btn-group px-3 h-full border-l ${!(value?.endDate === null && value?.startDate === null) && 'border-b border-pink-primary'}`}
      ref={dateRangeRef}
    >
      <button
        type="button"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        className={`flex items-center gap-3 ${isDatePickerOpen ? "show" : ""} `}
        aria-expanded={isDatePickerOpen}
        onClick={() => {
          setIsDatePickerOpen(!isDatePickerOpen);
        }}
      >
        <p>
          {value?.endDate === null && value?.startDate === null
            ? "Date range"
            : value?.startDate === value?.endDate
              ? `${new Date(value?.startDate).toLocaleDateString()}`
              : `${new Date(value?.startDate).toLocaleDateString()} to ${new Date(value?.endDate).toLocaleDateString()}`}
        </p>
        <svg
          width="14"
          height="15"
          viewBox="0 0 14 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.23457 0.5C4.66412 0.5 5.01235 0.848223 5.01235 1.27778V1.88272H8.98765V1.27778C8.98765 0.848223 9.33588 0.5 9.76543 0.5C10.195 0.5 10.5432 0.848223 10.5432 1.27778V1.88272H11.8395C12.4125 1.88272 12.962 2.11034 13.3672 2.51551C13.7724 2.92068 14 3.47021 14 4.04321V12.3395C14 12.9125 13.7724 13.462 13.3672 13.8672C12.962 14.2724 12.4125 14.5 11.8395 14.5H2.16049C1.58749 14.5 1.03796 14.2724 0.632794 13.8672C0.227623 13.462 0 12.9125 0 12.3395V4.04321C0 3.47021 0.227623 2.92068 0.632794 2.51551C1.03797 2.11034 1.5875 1.88272 2.16049 1.88272H3.45679V1.27778C3.45679 0.848223 3.80501 0.5 4.23457 0.5ZM3.45679 3.43827H2.16049C2.00005 3.43827 1.84619 3.50201 1.73274 3.61545C1.61929 3.7289 1.55556 3.88277 1.55556 4.04321V12.3395C1.55556 12.4999 1.61929 12.6538 1.73274 12.7673C1.84619 12.8807 2.00005 12.9444 2.16049 12.9444H11.8395C11.9999 12.9444 12.1538 12.8807 12.2673 12.7673C12.3807 12.6538 12.4444 12.4999 12.4444 12.3395V4.04321C12.4444 3.88277 12.3807 3.7289 12.2673 3.61545C12.1538 3.50201 11.9999 3.43827 11.8395 3.43827H10.5432V4.04321C10.5432 4.47276 10.195 4.82099 9.76543 4.82099C9.33588 4.82099 8.98765 4.47276 8.98765 4.04321V3.43827H5.01235V4.04321C5.01235 4.47276 4.66412 4.82099 4.23457 4.82099C3.80501 4.82099 3.45679 4.47276 3.45679 4.04321V3.43827ZM2.76543 6.80864C2.76543 6.37909 3.11366 6.03086 3.54321 6.03086H10.4568C10.8863 6.03086 11.2346 6.37909 11.2346 6.80864C11.2346 7.2382 10.8863 7.58642 10.4568 7.58642H3.54321C3.11366 7.58642 2.76543 7.2382 2.76543 6.80864Z"
            fill="#1A73E8"
          />
        </svg>
        {isDatePickerOpen ? <FaAngleUp /> : <FaAngleDown />}
      </button>

      <div
        className={`dropdown-menu dropdown-menu-end dropdown-menu-lg-end ${isDatePickerOpen ? "show" : ""
          }`}
        style={{
          right: "25px",
          top: "120%",
          position: "absolute",
        }}
      >
        {isDatePickerOpen ? (
          <div className="data-range-blk">
            <div className="d-flex">
              <div className="tab-content" id="v-pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="v-pills-daterange-1"
                  role="tabpanel"
                  aria-labelledby="v-pills-daterange-1-tab"
                  tabIndex={0}
                >
                  <div className="calander-blk  border-b border-border">
                    <DateRangePicker
                      ranges={[selectionRange]}
                      months={2}
                      onChange={(ranges) => {
                        setSelectionRange(ranges?.selection);
                      }}
                      direction="horizontal"
                    />
                  </div>
                  <div className="w-full flex justify-between items-centerp p-3">
                    <span className="daterange-note">
                      Select a date or a range to see the report
                    </span>
                    <div className="flex justify-center items-center gap-3">
                      <button
                        type="button"
                        onClick={handleResetDate}
                        className="btn bg-gray-light w-76"
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={applyDateRange}
                        className="btn btn-primary w-76"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

export default CustomDateRangePicker;
